import { Box, Typography, Paper, Grid, Chip, LinearProgress } from '@mui/material';
import { PieChart, BarChart } from '@mui/x-charts-pro';

export interface MetricCard {
  label: string;
  value: string | number;
  color?: string;
}

export interface PieChartData {
  label: string;
  value: number;
}

export interface ListPageHeaderProps {
  title?: string;
  action?: React.ReactNode;
  metrics?: MetricCard[];
  pieChartData?: PieChartData[];
  pieChartTitle?: string;
  barChartData?: number[];
  barChartLabels?: string[];
  barChartTitle?: string;
  barChartColors?: string[];
}

export const ListPageHeader = ({
  title,
  action,
  metrics = [],
  pieChartData,
  pieChartTitle,
  barChartData = [],
  barChartLabels = [],
  barChartTitle,
}: ListPageHeaderProps) => {
  return (
    <Box sx={{ mt: 2, mb: 3, px: 2 }}>
      {title && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'grey.900' }}>{title}</Typography>
          {action && <Box>{action}</Box>}
        </Box>
      )}
      {metrics.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {metrics.map((m, i) => (
            <Grid key={i} size={{ xs: 6, sm: 3, md: 2 }}>
              <Paper elevation={0} sx={{ p: 2, textAlign: 'center', borderTop: `4px solid ${m.color || 'primary.main'}`, bgcolor: 'action.hover' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {m.label}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: m.color || 'primary.main' }}>
                  {m.value}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {(pieChartData?.length || barChartData.length) && (
        <Grid container spacing={3}>
          {pieChartData && pieChartData.length > 0 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, display: 'block', fontWeight: 600 }}>
                  {pieChartTitle || 'Distribution'}
                </Typography>
                <Box sx={{ height: 150 }}>
                  <PieChart
                    series={[{
                      data: pieChartData.map((d, i) => ({ ...d, color: ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#757575', '#00acc1'][i % 6] })),
                      innerRadius: 28,
                      outerRadius: 60,
                    }]}
                    height={140}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, flexWrap: 'wrap', '& > *': { mx: 1 } }}>
                  {pieChartData.map((d, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#757575', '#00acc1'][i % 6] }} />
                      <Typography variant="caption" sx={{ fontSize: 10 }}>{d.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          )}

          {barChartData.length > 0 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, display: 'block', fontWeight: 600 }}>
                  {barChartTitle || 'Trend'}
                </Typography>
                <Box sx={{ height: 150 }}>
                  <BarChart
                    xAxis={[{ scaleType: 'band', data: barChartLabels }]}
                    series={[{ data: barChartData, color: '#1976d2' }]}
                    height={140}
                  />
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export const StatusSummaryChip = ({ status, count }: { status: string; count: number }) => (
  <Chip
    label={`${status}: ${count}`}
    size="small"
    sx={{ fontSize: 11, height: 20 }}
  />
);